import Vue from 'vue'
import Vuex from 'vuex'
import VueResource from 'vue-resource'

import studiesList from '../studies/studiesList'

Vue.use(Vuex)
Vue.use(VueResource)

// recursive object assign function, returns new object
const deepAssign = function (object, template) {
  for (let i in object) {
    if (typeof object[i] === 'object' && !Array.isArray(object[i]) && typeof template[i] === 'object') {
      object[i] = deepAssign(object[i], template[i])
    }
  }
  return Object.assign({}, template, object)
}

export default new Vuex.Store({
  state: {
    chosenStudy: null,
    userLang: null,
    userId: null,
    userAuthToken: null,
    studyConsent: null,
    userStudyIdentifier: null,
    qnBuddyBotId: null,
    studies: studiesList.studies
  },
  getters: {
    // builds a messages object using chosenStudy, userLang and commonLocalisations
    localisation: (state) => {
      let userLang = state.userLang
      let chosenStudy = state.chosenStudy
      if (chosenStudy !== null && userLang !== null) {
        if (chosenStudy.localisations.hasOwnProperty(userLang)) {
          return deepAssign(chosenStudy.localisations[userLang], studiesList.commonLocalisations[userLang])
        } else {
          console.error('userLang not within list of study languages!') // this should not be happening
          return deepAssign(chosenStudy.localisations[chosenStudy.defaultLang], studiesList.commonlocalisations[chosenStudy.defaultLang])
        }
      } else {
        return null
      }
    },
    proxyAuthUrl: (state) => {
      return process.env.VUE_APP_API_PROXY_URL + state.userId
    }
  },
  mutations: {
    setChosenStudy: (state, payload) => {
      state.chosenStudy = payload
    },
    setUserLang: (state, payload) => {
      state.userLang = payload
    },
    setUserParameters: (state, payload) => {
      state.userId = payload.userId
      state.userAuthToken = payload.userAuthToken
    },
    setStudyConsent: (state, payload) => {
      state.studyConsent = payload
    },
    setUserStudyIdentifier: (state, payload) => {
      state.userStudyIdentifier = payload
    },
    setQnBuddyBotId: (state, payload) => {
      state.qnBuddyBotId = payload
    }
  },
  actions: {
    updateUserParameters: ({ commit }, payload) => {
      return new Promise((resolve, reject) => {
        commit('setUserParameters', payload)
        resolve()
      })
    },
    updateUserLang: ({ commit }, payload) => {
      return new Promise((resolve, reject) => {
        commit('setUserLang', payload)
        resolve()
      })
    },
    updateUserLogout: ({ commit }) => {
      return new Promise((resolve, reject) => {
        commit('setUserParameters', {
          userId: null,
          userAuthToken: null
        })
        resolve()
      })
    },
    updateUserStudyIdentifier: ({ commit }, payload) => {
      return new Promise(resolve => {
        commit('setUserStudyIdentifier', payload)
        resolve()
      })
    },
    initializeChosenStudy: ({ commit, state }, payload) => {
      return new Promise((resolve, reject) => {
        if (state.studies.hasOwnProperty(payload)) {
          commit('setChosenStudy', state.studies[payload])
          commit('setUserLang', state.studies[payload].defaultLang)
          resolve()
        } else {
          reject(new Error('Study name not found.'))
        }
      })
    },
    // logs the user out, not the same as withdrawConsent
    declineConsent: ({ commit }) => {
      return new Promise((resolve, reject) => {
        commit('setUserParameters', {
          userId: null,
          userAuthToken: null
        })
        resolve()
      })
    },
    checkStudyConsent: ({ commit, state, getters }) => {
      let url = process.env.VUE_APP_API_ENDPOINT + 'studies/user/?study_name=' + state.chosenStudy.studyName + '&userid=' + state.userId
      return new Promise((resolve, reject) => {
        Vue.http.get(getters.proxyAuthUrl + '?timestamp=' + new Date().getTime(), {
          headers: {
            'request-url': url
          }
        }).then((response) => {
          let consent = JSON.parse(response.body).matched
          commit('setStudyConsent', consent)
          resolve()
        }, (error) => {
          reject(error)
        })
      })
    },
    checkStudyIdentifier: ({ commit, state, getters }) => {
      // ignores if the study doesn't require identifier
      // move validations like these one level up?
      if (!state.chosenStudy.requiresIdentifier) {
        return true
      }

      let url = process.env.VUE_APP_API_ENDPOINT + 'answer/standard?formid=' + state.chosenStudy.identifierFormId + '&userid=' + state.userId
      return new Promise((resolve, reject) => {
        Vue.http.get(getters.proxyAuthUrl + '?timestamp=' + new Date().getTime(), {
          headers: {
            'request-url': url
          }
        }).then((response) => {
          let parsedResponse = JSON.parse(response.body)
          if (parsedResponse.success) {
            let answers = parsedResponse.answer
            let payload = {}
            for (let i in answers) {
              payload[i] = answers[i].choice.label
            }
            commit('setUserStudyIdentifier', payload)
          }
          resolve()
        }, (error) => {
          if (error.status === 404) {
            resolve()
          } else {
            reject(error)
          }
        })
      })
    },
    submitUserStudyIdentifier: ({ commit }, payload) => {
      return new Promise((resolve, reject) => {
        Vue.http.post(process.env.VUE_APP_API_ENDPOINT + 'answer', payload).then(response => {
          resolve()
        }, (error) => {
          reject(error)
        })
      })
    },
    // this is causing problems for square-study, which isn't enabled right now
    fetchQnBuddyBotId ({ commit, state, getters }) {
      return true
      // just returns true if study has no recurring bots
      if (!state.chosenStudy.requiresBrazeRecurring) {
        return true
      }

      let url = process.env.VUE_APP_API_DAILY_SERVICE_HOST + 'v1/buddies/' + state.userId + '/find'
      return new Promise((resolve, reject) => {
        Vue.http.get(url).then((response) => {
          let userBotList = []
          response.body.result.forEach(bot => {
            userBotList.push(bot.id)
          })

          let studyBotList = userBotList.filter(element => state.chosenStudy.studyQbBotId.includes(element))
          let allBotList = userBotList.filter(element => JSON.parse(process.env.VUE_APP_QB_STUDY_BOT_ID).includes(element))

          let payload = null

          if (studyBotList.length === 0) {
            if (allBotList.length === 0) {
              Vue.Rollbar.critical('User is not friended with Qn Bot', state.userId)
            } else {
              payload = Math.max(...allBotList)
            }
          } else {
            payload = Math.max(...studyBotList)
          }

          let brazePayload = {}

          brazePayload[state.chosenStudy.brazeRecurringAttribute] = payload

          let brazeAttributesUrl = process.env.VUE_APP_API_DAILY_SERVICE_HOST + 'v1/braze/' + state.userId + '/attributes'

          Vue.http.post(brazeAttributesUrl, brazePayload).then(() => {
          }, (error) => {
            console.log(error)
            Vue.Rollbar.error('Updating Braze attributes failed', error, getters.brazeAttributesUrl)
          })
          commit('setQnBuddyBotId', payload)
          resolve()
        }, (error) => {
          reject(error)
        })
      })
    },
    matchUserToStudy ({ state, commit, getters }, payload) {
      let url = process.env.VUE_APP_API_ENDPOINT + 'studies/matchuser-new/?study_name=' + state.chosenStudy.studyName + '&userid=' + state.userId + '&lang=' + state.userLang + '&country=CH'
      if (payload !== true) {
        url = process.env.VUE_APP_API_ENDPOINT + 'studies/unmatchuser/?study_name=' + state.chosenStudy.studyName + '&userid=' + state.userId
      }

      return new Promise((resolve, reject) => {
        Vue.http.post(getters.proxyAuthUrl + '?timestamp=' + new Date().getTime(), {}, {
          headers: {
            'request-url': url
          }
        }).then(() => {
          commit('setStudyConsent', true)
          resolve()
        }, (error) => {
          reject(error)
        })
      })
    },
    updateBrazeConsent ({ state }, payload) {
      if (!state.chosenStudy.requiresBrazeConsent) {
        return true
      }

      return new Promise((resolve, reject) => {
        let brazeAttributesUrl = process.env.VUE_APP_API_DAILY_SERVICE_HOST + 'v1/braze/' + state.userId + '/attributes'
        let brazePayload = {}

        brazePayload[state.chosenStudy.brazeConsentAttribute] = payload

        Vue.http.post(brazeAttributesUrl, brazePayload).then(() => {
          resolve()
        }, (error) => {
          console.error(error)
          Vue.rollbar.error('Updating Braze attributes failed', error)
          reject(error)
        })
      })
    }
  }
})
